// PlayFirst Parent — Login. Same /api/auth/login flow but routes the user
// to the Parent App home if they're parent_self. Includes a self-contained
// "Forgot password?" 2-step flow (request code → reset).
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Heart, Loader2, ArrowLeft, KeyRound } from 'lucide-react';
import { PFP } from './styles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function ParentAppLogin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [busy, setBusy] = useState(false);

  // Forgot-password state — null = closed, 'request' = step 1, 'reset' = step 2.
  const [forgotStep, setForgotStep] = useState(null);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotCode, setForgotCode] = useState('');
  const [forgotNewPw, setForgotNewPw] = useState('');
  const [forgotBusy, setForgotBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/login`, {
        email: form.email.trim().toLowerCase(),
        password: form.password,
      });
      const user = res.data.user;
      // Dual-role acceptance: accept any of the three sources of truth
      // for PFP access so an old user record whose has_parent_app_access
      // flag was never stamped (pre iter-91 activation) still gets in.
      const hasPfp = (
        user.role === 'parent_self'
        || user.has_parent_app_access
        || user.capabilities?.pfp
        || (user.parent_subscription_status || '').toLowerCase() === 'active'
      );
      if (!hasPfp) {
        toast.error("This account doesn't have PlayFirst Parent access yet. If you've just paid, please try again in a moment — or contact support if this persists.");
        setBusy(false);
        return;
      }
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(user));
      toast.success(`Welcome back, ${user.name}`);
      // Feb 2026 — Default landing is ALWAYS the PFP "At Home" tab.
      // Rationale: users signing in via /parent-app/login expect the
      // parent product to open. The global DualRoleTabBar lets a
      // dual-role parent switch to "With my therapist" in one tap
      // without a re-login. Previously dual-role landed on `/`
      // (Clinical dashboard) which confused parents who thought they
      // needed to re-sign in to see the PFP surface.
      navigate('/parent-app/home');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  const openForgot = () => {
    setForgotStep('request');
    setForgotEmail(form.email);
    setForgotCode('');
    setForgotNewPw('');
  };

  const sendResetEmail = async (e) => {
    e?.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email: forgotEmail.trim().toLowerCase(),
      });
      toast.success('Check your inbox — the reset code is on its way.');
      setForgotStep('reset');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send reset email — please try again.");
    } finally {
      setForgotBusy(false);
    }
  };

  const submitReset = async (e) => {
    e?.preventDefault();
    if (forgotNewPw.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setForgotBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: forgotEmail.trim().toLowerCase(),
        code: forgotCode.trim().toUpperCase(),
        new_password: forgotNewPw,
      });
      toast.success('Password reset — please sign in with the new one.');
      setForm({ email: forgotEmail.trim().toLowerCase(), password: '' });
      setForgotStep(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid or expired code.');
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <div className={PFP.page}>
      <header className="px-4 sm:px-8 py-4 max-w-6xl mx-auto">
        <Link to="/parent-app" className="flex items-center gap-2 w-fit">
          <Heart className="w-6 h-6 text-[#7C6F9C] fill-[#7C6F9C]/20" />
          <span className="font-serif text-xl text-[#3a2f4f]">PlayFirst</span>
          <span className="text-xs uppercase tracking-wider text-[#7C6F9C] font-medium ml-1">
            Parent
          </span>
        </Link>
      </header>

      <main className="max-w-md mx-auto px-4 py-8">
        {!forgotStep ? (
          <>
            <h1 className={`${PFP.h2} mb-6`}>Welcome back</h1>
            <form onSubmit={handleSubmit} className={`${PFP.card} p-6 space-y-5`}>
              <div>
                <label className="block text-sm text-stone-600 mb-1">Email</label>
                <input
                  type="email" required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full h-12 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
                  data-testid="parent-login-email"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm text-stone-600">Password</label>
                  <button
                    type="button"
                    onClick={openForgot}
                    className="text-xs text-[#7C6F9C] hover:underline"
                    data-testid="parent-login-forgot-link"
                  >
                    Forgot password?
                  </button>
                </div>
                <input
                  type="password" required
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full h-12 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
                  data-testid="parent-login-password"
                />
              </div>
              <button
                type="submit" disabled={busy}
                className={PFP.btnPrimary + ' w-full inline-flex items-center justify-center gap-2'}
                data-testid="parent-login-submit"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {busy ? 'Signing in…' : 'Sign in'}
              </button>

              {/* Phase 1B — Google Sign-In (Emergent-managed OAuth).
                  Redirects to Emergent's auth screen which sends the user
                  back to /parent-app/auth-callback with #session_id=...
                  in the URL fragment. The callback route exchanges that
                  for our internal JWT via /api/auth/google/session. */}
              <div className="flex items-center gap-3 my-2">
                <div className="flex-1 h-px bg-stone-200" />
                <span className="text-xs text-stone-400 uppercase tracking-wide">or</span>
                <div className="flex-1 h-px bg-stone-200" />
              </div>
              <button
                type="button"
                onClick={() => {
                  const redirectUrl = `${window.location.origin}/parent-app/auth-callback`;
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="w-full h-11 rounded-full bg-white border border-stone-200 hover:border-stone-300 hover:bg-stone-50 text-stone-800 text-sm font-medium inline-flex items-center justify-center gap-3 transition-all"
                data-testid="parent-login-google"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Sign in with Google
              </button>

              <p className="text-center text-xs text-stone-500">
                New here?{' '}
                <Link to="/parent-app/signup" className="text-[#7C6F9C] hover:underline">
                  Create an account
                </Link>
              </p>
            </form>
          </>
        ) : forgotStep === 'request' ? (
          <>
            <button onClick={() => setForgotStep(null)} className="flex items-center gap-2 text-stone-500 hover:text-stone-800 mb-4 text-sm">
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </button>
            <h1 className={`${PFP.h2} mb-2`}>Forgot password</h1>
            <p className="text-sm text-stone-600 mb-5 leading-relaxed">
              Enter the email you signed up with — we&apos;ll send you a 6-character reset code.
            </p>
            <form onSubmit={sendResetEmail} className={`${PFP.card} p-6 space-y-5`}>
              <div>
                <label className="block text-sm text-stone-600 mb-1">Email</label>
                <input
                  type="email" required
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="w-full h-12 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
                  data-testid="parent-forgot-email"
                />
              </div>
              <button
                type="submit" disabled={forgotBusy}
                className={PFP.btnPrimary + ' w-full inline-flex items-center justify-center gap-2'}
                data-testid="parent-forgot-send-btn"
              >
                {forgotBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                {forgotBusy ? 'Sending…' : 'Send reset code'}
              </button>
              <p className="text-center text-xs text-stone-500">
                Still stuck?{' '}
                <Link to="/parent-app/contact" className="text-[#7C6F9C] hover:underline">
                  Email Michaela directly
                </Link>
              </p>
            </form>
          </>
        ) : (
          <>
            <button onClick={() => setForgotStep('request')} className="flex items-center gap-2 text-stone-500 hover:text-stone-800 mb-4 text-sm">
              <ArrowLeft className="w-4 h-4" /> Use a different email
            </button>
            <h1 className={`${PFP.h2} mb-2`}>Enter your reset code</h1>
            <p className="text-sm text-stone-600 mb-5 leading-relaxed">
              We sent a 6-character code to <strong>{forgotEmail}</strong>. Codes expire after 1 hour.
            </p>
            <form onSubmit={submitReset} className={`${PFP.card} p-6 space-y-5`}>
              <div>
                <label className="block text-sm text-stone-600 mb-1">Reset code</label>
                <input
                  required maxLength={8}
                  value={forgotCode}
                  onChange={(e) => setForgotCode(e.target.value.toUpperCase())}
                  className="w-full h-12 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none tracking-widest text-center font-mono uppercase"
                  placeholder="ABCD1234"
                  data-testid="parent-forgot-code"
                />
              </div>
              <div>
                <label className="block text-sm text-stone-600 mb-1">New password</label>
                <input
                  type="password" required minLength={6}
                  value={forgotNewPw}
                  onChange={(e) => setForgotNewPw(e.target.value)}
                  className="w-full h-12 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
                  placeholder="At least 6 characters"
                  data-testid="parent-forgot-new-password"
                />
              </div>
              <button
                type="submit" disabled={forgotBusy}
                className={PFP.btnPrimary + ' w-full inline-flex items-center justify-center gap-2'}
                data-testid="parent-forgot-submit-btn"
              >
                {forgotBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                {forgotBusy ? 'Updating…' : 'Reset password'}
              </button>
              <button
                type="button"
                onClick={sendResetEmail}
                disabled={forgotBusy}
                className="text-xs text-stone-500 hover:text-[#7C6F9C] block mx-auto"
              >
                Didn&apos;t get the code? Resend
              </button>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
